import React from 'react'
import { Link } from '@inertiajs/react'
import { Crown, Layers } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  followerMembers,
  groupHotelCount,
  isUnitScope,
  subjectIsPlural,
  unitCoverageSentence,
  type MaybeScope,
} from '@/lib/reporting-scope'

interface UnitScopeNoteProps {
  scope: MaybeScope
  /**
   * What the page's pooled figures actually are, in the operator's words — "Occupancy and pace",
   * "Revenue, pace and targets". Replaces the generic "Figures on this page" so the note answers
   * the question the operator has in front of him rather than a general one.
   */
  covers?: string
  /** Names every hotel in the unit and links to each. Off where the page is already dense. */
  listHotels?: boolean
  className?: string
}

/**
 * "THESE NUMBERS COVER THE WHOLE UNIT" — said once, at the top of a lead's reporting surface.
 *
 * A lead's dashboard, calendar and Targets page pool every hotel in the group: bookings over
 * pooled capacity, one booking curve, one target. That is the point of the feature and it is
 * invisible in the numbers themselves, so the operator must never have to wonder whose figure he
 * is reading. Deliberately a quiet inline note and NOT a banner: the layout's banner rail means a
 * fault (a failed push, a dead PMS connection) and combined inventory is not a fault.
 *
 * Renders nothing unless this property leads a group of more than one hotel — so an ungrouped page
 * is untouched.
 */
export default function UnitScopeNote({ scope, covers, listHotels = false, className }: UnitScopeNoteProps) {
  if (!isUnitScope(scope)) return null
  if (groupHotelCount(scope) < 2) return null

  const sentence = covers
    ? `${covers} ${subjectIsPlural(covers) ? 'cover' : 'covers'} all ${hotelPhrase(scope)} in this combined inventory.`
    : unitCoverageSentence(scope)
  const followers = followerMembers(scope)

  return (
    <div
      className={cn(
        'flex flex-wrap items-start gap-x-3 gap-y-1.5 rounded-xl border border-primary/20 bg-primary/5 px-4 py-2.5',
        className,
      )}
    >
      <Layers className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-foreground">{sentence}</p>
        {listHotels && followers.length > 0 && (
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 font-medium text-primary">
              <Crown className="h-2.5 w-2.5" />
              {scope.leadPropertyName}
            </span>
            {followers.map((member) => (
              <Link
                key={member.id}
                href={member.basePath}
                className="underline decoration-border underline-offset-2 transition-colors hover:text-foreground"
              >
                {member.name}
              </Link>
            ))}
          </p>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">Each listing still publishes its own prices to the PMS.</p>
    </div>
  )
}

function hotelPhrase(scope: MaybeScope): string {
  const n = groupHotelCount(scope)
  return n === 1 ? '1 listing' : `${n} listings`
}
